/**
 * Connect SharePoint Step - OAuth consent + enter Site URLs for health check
 * Admin enters the SharePoint site URLs they want to scan.
 * First site = primary site (hosts WVHealth_ToolConfig list)
 */

import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useWriteAccess } from '../../hooks/useWriteAccess';
import { getCurrentAccount } from '../../services/authService';
import { getLicensedSites } from '../../services/onboardingService';
import {
  verifySiteOwnership,
  provisionAllRequiredLists,
  saveConfiguredSitesToToolConfig,
} from '../../services/sharePointService';
import { ComingSoonNotice } from '../../components/layout/ComingSoon';

interface SiteEntry {
  url: string;
  status: 'validating' | 'provisioning' | 'ready' | 'error';
  isPrimary: boolean;
  siteName?: string;
  error?: string;
}

interface ConnectSharePointProps {
  initialSites?: SiteEntry[];
  initialConnectionState?: 'NotStarted' | 'Connecting' | 'Connected' | 'Failed';
  onNext: (data: {
    connectionState: string;
    selectedSites: SiteEntry[];
    primarySiteUrl: string;
  }) => void;
  onBack: () => void;
}

export default function ConnectSharePoint({
  initialSites,
  initialConnectionState,
  onNext,
  onBack,
}: ConnectSharePointProps) {
  const { instance } = useMsal();
  const { canWrite, isReadOnly } = useWriteAccess();
  const [connectionState, setConnectionState] = useState<
    'NotStarted' | 'Connecting' | 'Connected' | 'Failed'
  >(initialConnectionState || 'NotStarted');
  const [error, setError] = useState<string | null>(null);

  // Site URL entry state
  const [siteUrl, setSiteUrl] = useState('');
  const [sites, setSites] = useState<SiteEntry[]>(initialSites || []);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isAddingSite, setIsAddingSite] = useState(false);
  const [isLoadingLicensedSites, setIsLoadingLicensedSites] = useState(false);
  const [licensedSiteUrls, setLicensedSiteUrls] = useState<string[]>([]);
  const [licensedSitesLoaded, setLicensedSitesLoaded] = useState(false);

  const handleConnect = async () => {
    if (!canWrite) {
      setError(
        'Cannot connect SharePoint with an expired license. Please renew your license first.',
      );
      return;
    }

    setConnectionState('Connecting');
    setError(null);

    try {
      // Request silent token first to avoid unnecessary popups
      try {
        await instance.acquireTokenSilent({
          scopes: ['Sites.Read.All', 'Sites.ReadWrite.All'],
          account: instance.getAllAccounts()[0],
        });
        setConnectionState('Connected');
      } catch (silentError) {
        // Fallback to popup
        await instance.acquireTokenPopup({
          scopes: ['Sites.Read.All', 'Sites.ReadWrite.All'],
        });
        setConnectionState('Connected');
      }
    } catch (err: any) {
      setConnectionState('Failed');
      setError(err.message || 'Failed to connect to SharePoint');
    }
  };

  // Auto-connect if we already have a token
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const account = instance.getAllAccounts()[0];
        if (account) {
          await instance.acquireTokenSilent({
            scopes: ['Sites.Read.All', 'Sites.ReadWrite.All'],
            account: account,
          });
          setConnectionState('Connected');
        }
      } catch {
        // Silent fail, user can click button
      }
    };
    checkConnection();
  }, [instance]);

  // Load licensed sites from License API when connected (first time only)
  useEffect(() => {
    const loadFromLicense = async () => {
      // Only run once, when connected, and only if no sites are already loaded
      if (
        connectionState !== 'Connected' ||
        licensedSitesLoaded ||
        isLoadingLicensedSites
      )
        return;
      if (initialSites && initialSites.length > 0) {
        // Sites already restored from session — no need to re-fetch from license
        setLicensedSitesLoaded(true);
        return;
      }

      setIsLoadingLicensedSites(true);
      console.log(
        '[ConnectSharePoint] Loading licensed sites from License API...',
      );
      try {
        const urls = await getLicensedSites();
        console.log(
          `[ConnectSharePoint] License API returned ${urls.length} site(s):`,
          urls,
        );
        setLicensedSiteUrls(urls);

        if (urls.length > 0 && sites.length === 0) {
          // Auto-populate sites from license — validate + provision primary
          await autoProvisionLicensedSites(urls);
        }
      } catch (err) {
        console.warn(
          '[ConnectSharePoint] Could not load licensed sites from License API:',
          err,
        );
      } finally {
        setIsLoadingLicensedSites(false);
        setLicensedSitesLoaded(true);
      }
    };
    loadFromLicense();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState]);

  /**
   * Auto-provision sites from the License API allowedSiteUrls.
   * First URL becomes the primary site (provisions WVHealth_ToolConfig list).
   * Remaining URLs are verified for ownership only.
   */
  const autoProvisionLicensedSites = async (urls: string[]) => {
    const account = getCurrentAccount();
    const tenantId = account?.tenantId || 'unknown';
    const orgName = account?.name || 'Unknown Organization';

    // Seed all sites as 'validating'
    const seedEntries: SiteEntry[] = urls.map((url, idx) => ({
      url,
      status: 'validating' as const,
      isPrimary: idx === 0,
    }));
    setSites(seedEntries);

    const finalEntries: SiteEntry[] = [...seedEntries];

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const isPrimary = i === 0;

      // Verify ownership
      const ownershipResult = await verifySiteOwnership(url);
      if (!ownershipResult.isOwner) {
        finalEntries[i] = {
          ...finalEntries[i],
          status: 'error',
          error:
            ownershipResult.error ||
            'You must be a Site Collection Owner of this site',
          siteName: ownershipResult.siteName,
        };
        setSites([...finalEntries]);
        continue;
      }

      finalEntries[i] = {
        ...finalEntries[i],
        siteName: ownershipResult.siteName,
      };

      if (isPrimary) {
        // Provision all required lists (WVHealth_ToolConfig, WVHealth_CoreSettings,
        // WV_SearchEmbeddingsCache) on the primary site during onboarding.
        finalEntries[i] = { ...finalEntries[i], status: 'provisioning' };
        setSites([...finalEntries]);

        const provisionResult = await provisionAllRequiredLists(
          url,
          tenantId,
          orgName,
          urls,
        );

        if (!provisionResult.success) {
          finalEntries[i] = {
            ...finalEntries[i],
            status: 'error',
            error:
              provisionResult.error || 'Failed to provision required lists',
          };
          setSites([...finalEntries]);
          continue;
        }
      }

      finalEntries[i] = { ...finalEntries[i], status: 'ready' };
      setSites([...finalEntries]);
    }

    // Save final site list to WVHealth_ToolConfig
    const primarySite = finalEntries.find(
      (s) => s.isPrimary && s.status === 'ready',
    );
    if (primarySite) {
      const readySites = finalEntries.filter((s) => s.status === 'ready');
      await saveConfiguredSitesToToolConfig(
        primarySite.url,
        readySites,
        primarySite.url,
      ).catch((err) =>
        console.warn(
          '[ConnectSharePoint] Failed to save licensed sites to ToolConfig:',
          err,
        ),
      );
    }
  };

  // Load configured sites from WVHealth_ToolConfig list on mount (fallback if license has no URLs)

  /** Validate SharePoint URL format and uniqueness */
  const validateUrl = (url: string): string | null => {
    const trimmed = url.trim();
    if (!trimmed) return 'Please enter a site URL';
    try {
      const parsed = new URL(trimmed);
      if (!parsed.hostname.endsWith('.sharepoint.com')) {
        return 'URL must be a SharePoint site (e.g. https://contoso.sharepoint.com/sites/hr)';
      }
    } catch {
      return 'Please enter a valid URL starting with https://';
    }
    if (sites.some((s) => s.url.toLowerCase() === trimmed.toLowerCase())) {
      return 'This site has already been added';
    }
    return null;
  };

  const handleAddSite = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateUrl(siteUrl);
    if (validationError) {
      setUrlError(validationError);
      return;
    }
    setUrlError(null);

    const trimmedUrl = siteUrl.trim();
    const isPrimary = sites.length === 0;

    // Add site with 'validating' status
    const newSite: SiteEntry = {
      url: trimmedUrl,
      status: 'validating',
      isPrimary,
    };
    setSites((prev) => [...prev, newSite]);
    setSiteUrl('');
    setIsAddingSite(true);

    try {
      // Step 1: Verify ownership
      const ownershipResult = await verifySiteOwnership(trimmedUrl);

      if (!ownershipResult.isOwner) {
        // Not an owner - mark as error
        setSites((prev) =>
          prev.map((s) =>
            s.url === trimmedUrl
              ? {
                  ...s,
                  status: 'error',
                  error:
                    ownershipResult.error ||
                    'You must be a Site Collection Owner of this site',
                  siteName: ownershipResult.siteName,
                }
              : s,
          ),
        );
        setIsAddingSite(false);
        return;
      }

      // Update with site name
      setSites((prev) =>
        prev.map((s) =>
          s.url === trimmedUrl
            ? { ...s, siteName: ownershipResult.siteName }
            : s,
        ),
      );

      // Step 2: If primary site, provision all required lists
      if (isPrimary) {
        setSites((prev) =>
          prev.map((s) =>
            s.url === trimmedUrl ? { ...s, status: 'provisioning' } : s,
          ),
        );

        const account = getCurrentAccount();
        const tenantId = account?.tenantId || 'unknown';
        const orgName = account?.name || 'Unknown Organization';

        const provisionResult = await provisionAllRequiredLists(
          trimmedUrl,
          tenantId,
          orgName,
          [trimmedUrl],
        );

        if (!provisionResult.success) {
          setSites((prev) =>
            prev.map((s) =>
              s.url === trimmedUrl
                ? {
                    ...s,
                    status: 'error',
                    error:
                      provisionResult.error ||
                      'Failed to provision required lists',
                  }
                : s,
            ),
          );
          setIsAddingSite(false);
          return;
        }
      }

      // Mark as ready
      setSites((prev) => {
        const updated = prev.map((s) =>
          s.url === trimmedUrl ? { ...s, status: 'ready' as const } : s,
        );

        // Save to WVHealth_ToolConfig after state update
        const saveSitesToToolConfig = async () => {
          const primarySite = updated.find((s) => s.isPrimary);
          if (primarySite) {
            const saveResult = await saveConfiguredSitesToToolConfig(
              primarySite.url,
              updated,
              primarySite.url,
            );

            if (!saveResult.success) {
              console.warn(
                '[ConnectSharePoint] Failed to save sites to ToolConfig:',
                saveResult.error,
              );
            } else {
              console.log(
                '[ConnectSharePoint] Successfully saved sites to ToolConfig',
              );
            }
          }
        };

        // Fire and forget save operation
        saveSitesToToolConfig().catch((err) => {
          console.error('[ConnectSharePoint] Error saving sites:', err);
        });

        return updated;
      });
    } catch (err: any) {
      setSites((prev) =>
        prev.map((s) =>
          s.url === trimmedUrl
            ? {
                ...s,
                status: 'error',
                error: err.message || 'Failed to add site',
              }
            : s,
        ),
      );
    } finally {
      setIsAddingSite(false);
    }
  };

  const handleRemoveSite = async (url: string) => {
    // Remove from sites array
    const updatedSites = sites.filter((s) => s.url !== url);
    setSites(updatedSites);

    // Save to WVHealth_ToolConfig after removal
    const primarySite = updatedSites.find((s) => s.isPrimary);
    if (primarySite) {
      try {
        const saveResult = await saveConfiguredSitesToToolConfig(
          primarySite.url,
          updatedSites,
          primarySite.url,
        );

        if (!saveResult.success) {
          console.warn(
            '[ConnectSharePoint] Failed to save sites after removal:',
            saveResult.error,
          );
        }
      } catch (error) {
        console.error(
          '[ConnectSharePoint] Error saving sites after removal:',
          error,
        );
      }
    }
  };

  /** Extract a friendly display name from a SharePoint URL */
  const getSiteName = (url: string): string => {
    try {
      const parts = new URL(url).pathname.split('/').filter(Boolean);
      return parts[parts.length - 1] || new URL(url).hostname;
    } catch {
      return url;
    }
  };

  const handleNext = () => {
    if (connectionState === 'Connected' && sites.length > 0) {
      const primarySite = sites.find((s) => s.isPrimary);
      if (!primarySite) {
        console.error('No primary site found');
        return;
      }

      // Only proceed if all sites are ready
      const allReady = sites.every((s) => s.status === 'ready');
      if (!allReady) {
        return;
      }

      onNext({
        connectionState: 'Connected',
        selectedSites: sites,
        primarySiteUrl: primarySite.url,
      });
    }
  };

  return (
    <div className='bg-white shadow sm:rounded-lg'>
      <div className='px-4 py-5 sm:p-6'>
        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
          Connect to SharePoint
        </h2>

        {/* ── Section 1: OAuth Consent ── */}
        <div className='space-y-4'>
          <p className='text-gray-600'>
            Grant permission to access your SharePoint sites, then add the site
            URLs you'd like to run the health check on.
          </p>

          {connectionState === 'NotStarted' && (
            <>
              <div className='rounded-md bg-blue-50 p-4'>
                <div className='flex'>
                  <div className='flex-shrink-0'>
                    <svg
                      className='h-5 w-5 text-blue-400'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <div className='ml-3 flex-1'>
                    <p className='text-sm text-blue-700'>
                      You'll be prompted to grant permissions in a popup window.
                      All content analysis runs locally in your browser.
                    </p>
                  </div>
                </div>
              </div>
              {isReadOnly && (
                <div className='rounded-md bg-yellow-50 p-4'>
                  <p className='text-sm text-yellow-700'>
                    Your license has expired. SharePoint connection requires an
                    active license.
                  </p>
                </div>
              )}
              <button
                onClick={handleConnect}
                disabled={!canWrite}
                type='button'
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  canWrite
                    ? 'bg-[#2ba6bb] hover:bg-[#228a9d] focus:ring-[#2ba6bb]'
                    : 'bg-gray-300 cursor-not-allowed'
                } focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                Connect SharePoint
              </button>
            </>
          )}

          {connectionState === 'Connecting' && (
            <div className='rounded-md bg-yellow-50 p-4'>
              <p className='text-sm text-yellow-700'>Requesting permissions…</p>
            </div>
          )}

          {connectionState === 'Connected' && (
            <div className='rounded-md bg-[#2ba6bb]/10 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-[#2ba6bb]'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <p className='text-sm text-[#228a9d]'>
                    Successfully connected to SharePoint!
                  </p>
                </div>
              </div>
            </div>
          )}

          {connectionState === 'Failed' && error && (
            <div className='rounded-md bg-red-50 p-4'>
              <p className='text-sm text-red-700'>{error}</p>
              <button
                onClick={handleConnect}
                className='mt-2 text-sm text-red-700 underline'
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* ── Section 2: Add Site URLs (shown after connected) ── */}
        {connectionState === 'Connected' && (
          <div className='mt-8'>
            {/* Licensed sites loading indicator */}
            {isLoadingLicensedSites && (
              <div className='mb-4 rounded-md bg-blue-50 p-4 flex items-center gap-3'>
                <svg
                  className='animate-spin h-5 w-5 text-[#2ba6bb] flex-shrink-0'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                <p className='text-sm text-blue-700'>
                  Loading your licensed sites from the License API…
                </p>
              </div>
            )}

            {/* Licensed sites banner — shown once loaded */}
            {licensedSitesLoaded &&
              licensedSiteUrls.length > 0 &&
              !isLoadingLicensedSites && (
                <div className='mb-4 rounded-md bg-green-50 border border-green-200 p-4'>
                  <div className='flex items-start gap-2'>
                    <svg
                      className='h-5 w-5 text-green-500 flex-shrink-0 mt-0.5'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                    <div>
                      <p className='text-sm font-medium text-green-800'>
                        {licensedSiteUrls.length} licensed site
                        {licensedSiteUrls.length !== 1 ? 's' : ''} loaded from
                        your license
                      </p>
                      <p className='text-xs text-green-700 mt-0.5'>
                        These sites have been pre-populated from License
                        Management System.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* No licensed sites — prompt manual entry */}
            {licensedSitesLoaded &&
              licensedSiteUrls.length === 0 &&
              !isLoadingLicensedSites && (
                <div className='text-center mb-6'>
                  <svg
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 48 48'
                    aria-hidden='true'
                    className='mx-auto h-12 w-12 text-gray-400'
                  >
                    <path
                      d='M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z'
                      strokeWidth={2}
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M4 24h40M24 4c5.523 8 8 16 0 20s-5.523 8-8 16 0-20'
                      strokeWidth={2}
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <h3 className='mt-2 text-base font-semibold text-gray-900'>
                    Add SharePoint Sites
                  </h3>
                  <p className='mt-1 text-sm text-gray-500'>
                    No sites were found in your license. Enter the full URL of
                    each SharePoint site you want to run the health check on.
                  </p>
                </div>
              )}

            {/* URL input row — only one site (the primary) is supported for now */}
            {sites.length === 0 && (
              <form onSubmit={handleAddSite} className='mt-6 flex'>
                <div className='flex-1'>
                  <input
                    type='url'
                    value={siteUrl}
                    onChange={(e) => {
                      setSiteUrl(e.target.value);
                      if (urlError) setUrlError(null);
                    }}
                    disabled={isAddingSite}
                    placeholder='https://contoso.sharepoint.com/sites/intranet'
                    aria-label='SharePoint site URL'
                    className={`block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#2ba6bb] ${
                      urlError ? 'outline-red-300' : 'outline-gray-300'
                    } ${isAddingSite ? 'opacity-50' : ''}`}
                  />
                  {urlError && (
                    <p className='mt-1 text-xs text-red-600'>{urlError}</p>
                  )}
                </div>
                <button
                  type='submit'
                  disabled={isAddingSite}
                  className={`ml-4 shrink-0 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2ba6bb] ${
                    isAddingSite
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#2ba6bb] hover:bg-[#228a9d]'
                  }`}
                >
                  {isAddingSite ? 'Adding...' : 'Add Site'}
                </button>
              </form>
            )}

            {sites.length > 0 && (
              <div className='mt-6'>
                <ComingSoonNotice message="Scanning multiple SharePoint sites isn't supported yet, each tenant currently connects to a single primary site. Multi-site support is planned." />
              </div>
            )}

            {/* Added sites list */}
            {sites.length > 0 && (
              <div className='mt-6'>
                <h4 className='text-sm font-medium text-gray-500'>
                  Sites to scan ({sites.length})
                </h4>
                <ul
                  role='list'
                  className='mt-3 divide-y divide-gray-200 border-t border-b border-gray-200'
                >
                  {sites.map((site) => (
                    <li
                      key={site.url}
                      className='flex items-center justify-between py-3'
                    >
                      <div className='flex min-w-0 flex-1 items-center space-x-3'>
                        <div className='flex-shrink-0'>
                          {site.status === 'validating' ||
                          site.status === 'provisioning' ? (
                            <span className='inline-flex h-8 w-8 items-center justify-center'>
                              <svg
                                className='animate-spin h-5 w-5 text-[#2ba6bb]'
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                              >
                                <circle
                                  className='opacity-25'
                                  cx='12'
                                  cy='12'
                                  r='10'
                                  stroke='currentColor'
                                  strokeWidth='4'
                                ></circle>
                                <path
                                  className='opacity-75'
                                  fill='currentColor'
                                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                ></path>
                              </svg>
                            </span>
                          ) : site.status === 'error' ? (
                            <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100'>
                              <svg
                                className='h-4 w-4 text-red-600'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M6 18L18 6M6 6l12 12'
                                />
                              </svg>
                            </span>
                          ) : (
                            <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2ba6bb]/10'>
                              <svg
                                className='h-4 w-4 text-[#2ba6bb]'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M5 13l4 4L19 7'
                                />
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center gap-2'>
                            <p className='truncate text-sm font-medium text-gray-900'>
                              {site.siteName || getSiteName(site.url)}
                            </p>
                            {site.isPrimary && (
                              <span className='inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800'>
                                Primary
                              </span>
                            )}
                          </div>
                          <p className='truncate text-xs text-gray-500'>
                            {site.url}
                          </p>
                          {site.status === 'validating' && (
                            <p className='text-xs text-yellow-600 mt-0.5'>
                              Verifying ownership...
                            </p>
                          )}
                          {site.status === 'provisioning' && (
                            <p className='text-xs text-blue-600 mt-0.5'>
                              Provisioning required lists (this may take a
                              moment)…
                            </p>
                          )}
                          {site.status === 'error' && site.error && (
                            <p className='text-xs text-red-600 mt-0.5'>
                              {site.error}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className='flex-shrink-0 ml-4'>
                        <button
                          type='button'
                          onClick={() => handleRemoveSite(site.url)}
                          disabled={
                            site.status === 'validating' ||
                            site.status === 'provisioning'
                          }
                          className={`inline-flex items-center text-sm font-medium ${
                            site.status === 'validating' ||
                            site.status === 'provisioning'
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-800'
                          }`}
                        >
                          <svg
                            className='h-4 w-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                            />
                          </svg>
                          <span className='ml-1'>Remove</span>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ── Navigation ── */}
        <div className='mt-8 flex items-center justify-between'>
          <button
            onClick={onBack}
            type='button'
            className='inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ba6bb]'
          >
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={
              connectionState !== 'Connected' ||
              sites.length === 0 ||
              !sites.every((s) => s.status === 'ready')
            }
            type='button'
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
              connectionState === 'Connected' &&
              sites.length > 0 &&
              sites.every((s) => s.status === 'ready')
                ? 'bg-[#2ba6bb] hover:bg-[#228a9d] focus:ring-[#2ba6bb]'
                : 'bg-gray-300 cursor-not-allowed'
            } focus:outline-none focus:ring-2 focus:ring-offset-2`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
